/**
 * Lite 群組／Square 設定 API
 * 更新群組設定、取得/更新 Square 設定、上傳發佈封面圖
 */

import { getApiBase } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

const activityPublishConfigShape = {
  enabled: undefined as boolean | undefined,
  title: undefined as string | undefined,
  content: undefined as string | undefined,
  buttonText: undefined as string | undefined,
  imageUrl: undefined as string | null | undefined,
  prefix: undefined as string | undefined,
  includeActivityTitle: undefined as boolean | undefined,
  includeActivityDescription: undefined as boolean | undefined,
  includeJoinUrl: undefined as boolean | undefined,
  suffix: undefined as string | undefined,
};

const userReplyFormatShape = {
  autoReplyEnabled: undefined as boolean | undefined,
  prefix: undefined as string | undefined,
  operationReply: undefined as boolean | undefined,
  remainingBelowThreshold: undefined as number | null | undefined,
  suffix: undefined as string | undefined,
  includeActivityUrl: undefined as boolean | undefined,
};

export type LiteGroupSettingsData = {
  name: string;
  type: string | null;
  activityPublishConfig?: typeof activityPublishConfigShape | null;
  userReplyFormat?: typeof userReplyFormatShape | null;
};

export async function updateLiteGroupSettings(
  groupId: string,
  jwtToken: string,
  data: LiteGroupSettingsData,
  lineIdToken?: string
): Promise<void> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const url = `${apiBase}/api/lite/auth/group-settings?groupId=${encodeURIComponent(groupId)}`;
  const body: Record<string, unknown> = { name: data.name, type: data.type };
  if (data.activityPublishConfig !== undefined)
    body.activityPublishConfig = data.activityPublishConfig;
  if (data.userReplyFormat !== undefined)
    body.userReplyFormat = data.userReplyFormat;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwtToken}`,
  };
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  const response = await apiFetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '儲存設定失敗，請稍後再試',
    });
  }
}

export async function getSquareSettings(
  suffix: string,
  jwtToken: string
): Promise<{
  name: string;
  type: string | null;
  activityPublishConfig: typeof activityPublishConfigShape | null;
  userReplyFormat: typeof userReplyFormatShape | null;
}> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const url = `${apiBase}/api/lite/auth/square-settings?suffix=${encodeURIComponent(suffix.trim().toUpperCase())}`;
  const response = await liteApiFetch(
    url,
    { method: 'GET', headers: { Authorization: `Bearer ${jwtToken}` } },
    { squareSuffix: suffix.trim().toUpperCase() }
  );

  if (!response.ok) {
    if (response.status === 404) throw new Error('Square 不存在或您不是擁有者');
    await handleLiteErrorResponse(response, { defaultMessage: '取得設定失敗' });
  }

  return (await response.json()) as Promise<{
    name: string;
    type: string | null;
    activityPublishConfig: typeof activityPublishConfigShape | null;
    userReplyFormat: typeof userReplyFormatShape | null;
  }>;
}

export async function updateLiteSquareSettings(
  suffix: string,
  jwtToken: string,
  data: {
    name: string;
    type: string | null;
    activityPublishConfig?: typeof activityPublishConfigShape | null;
    userReplyFormat?: typeof userReplyFormatShape | null;
  }
): Promise<void> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');
  const url = `${apiBase}/api/lite/auth/square-settings?suffix=${encodeURIComponent(suffix.trim().toUpperCase())}`;
  const body: Record<string, unknown> = {
    name: data.name,
    type: data.type,
  };
  if (data.activityPublishConfig !== undefined)
    body.activityPublishConfig = data.activityPublishConfig;
  if (data.userReplyFormat !== undefined)
    body.userReplyFormat = data.userReplyFormat;

  const response = await liteApiFetch(
    url,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(body),
    },
    { squareSuffix: suffix.trim().toUpperCase() }
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '儲存設定失敗，請稍後再試',
    });
  }
}

export async function uploadLiteFlexCover(
  groupId: string,
  jwtToken: string,
  file: File
): Promise<{ public_url: string }> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const formData = new FormData();
  formData.append('file', file);
  const url = `${apiBase}/api/lite/auth/upload-flex-cover?groupId=${encodeURIComponent(groupId)}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwtToken}` },
    body: formData,
  });

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '上傳封面圖失敗，請稍後再試',
    });
  }
  const result = (await response.json()) as { public_url: string };
  return { public_url: result.public_url };
}
